import { gql } from '@apollo/client';
import type { RankingConfig, RankingPlatform, RankingReportInput } from '@/lib/campaigns/rankingCompetition';
import type { CpmCampaignRankingQuery } from '@/generated/cpm-ranking';

export type { RankingReportInput };
type RankingResult = CpmCampaignRankingQuery['cpmCampaignRanking'];
export type RankingRow = RankingResult['rows'][number];
export type RankingParticipantSummary = NonNullable<RankingResult['participant_summary']>;
type AccountReliability = RankingResult['admin_reports'][number]['account_reliability'];
/**
 * Reviewer rows also carry the publishing account's reliability; participants' own rows never do.
 * `connected_account` stays optional so a row without it (older backend) reads as not connected,
 * and `disqualified` so a row without it reads as not disqualified.
 */
export type RankingReport = Omit<RankingResult['own_reports'][number], 'platform' | 'connected_account' | 'disqualified'> & {
  platform: RankingPlatform; connected_account?: boolean | null; account_reliability?: AccountReliability;
  disqualified?: boolean | null;
};
export type CampaignRanking = Omit<RankingResult, 'config' | 'own_reports' | 'admin_reports'> & {
  config: RankingConfig;
  own_reports: RankingReport[];
  admin_reports: RankingReport[];
};

export const CPM_CAMPAIGN_RANKING = gql`
  query CpmCampaignRanking($campaignId: ID!) {
    cpmCampaignRanking(campaignId: $campaignId) {
      campaign_id campaign_type config phase can_review can_submit
      review_fingerprint pending_reports_count pending_clip_count pending_metrics_count can_finalize unresolved_ties finalized_at winner_contact_url last_view_sync_at
      rows { participant_id display_name total_views valid_post_count eligible place prize_cents is_self tie_pending standing_index }
      my_row { participant_id display_name total_views valid_post_count eligible place prize_cents is_self tie_pending standing_index }
      participant_summary {
        total_participants counted_views counted_post_count
        top_rows { participant_id display_name total_views valid_post_count eligible place prize_cents is_self tie_pending standing_index }
        nearby_rows { participant_id display_name total_views valid_post_count eligible place prize_cents is_self tie_pending standing_index }
        by_platform { platform counted_views counted_post_count }
        by_publication_day { date counted_views counted_post_count }
      }
      my_progress { target_type current_position next_position views_needed estimated_clips_needed }
      own_reports {
        id submission_id participant_id platform url profile_handle profile_url published_at reported_views evidence_url
        status verified_views profile_verified ownership_verified connected_account review_reason reviewed_at created_at admission_approved_at rules_check last_view_sync_at
        clip_approval ranked_views views_source views_captured_at metrics_status
        tracked_views tracked_views_captured_at tracked_views_source disqualified
      }
      admin_reports {
        id submission_id participant_id platform url profile_handle profile_url published_at reported_views evidence_url
        status verified_views profile_verified ownership_verified connected_account review_reason reviewed_at created_at admission_approved_at rules_check last_view_sync_at
        clip_approval ranked_views views_source views_captured_at metrics_status
        tracked_views tracked_views_captured_at tracked_views_source disqualified
        account_reliability { status score engagement_rate lifetime_views tracked_content username }
      }
    }
  }
`;
export const SUBMIT_CPM_RANKING_REPORT = gql`
  mutation SubmitMyCpmRankingReport($campaignId: ID!, $input: CpmRankingReportInput!) {
    submitMyCpmRankingReport(campaignId: $campaignId, input: $input) { id status }
  }
`;
export const REVIEW_CPM_RANKING_REPORT = gql`
  mutation AdminReviewCpmRankingReport($id: ID!, $input: CpmRankingReviewInput!) {
    adminReviewCpmRankingReport(id: $id, input: $input) { id status verified_views }
  }
`;
export const REVIEW_CPM_RANKING_SUBMISSION = gql`
  mutation AdminReviewCpmRankingSubmission($id: ID!, $input: CpmRankingReviewInput!) {
    adminReviewCpmRankingSubmission(id: $id, input: $input) { id submission_id status verified_views }
  }
`;
export const REVIEW_CPM_RANKING_CLIP = gql`
  mutation AdminReviewCpmRankingClip($id: ID!, $approval: String!, $reason: String, $ownershipVerified: Boolean) {
    adminReviewCpmRankingClip(id: $id, approval: $approval, reason: $reason, ownershipVerified: $ownershipVerified) { id submission_id clip_approval ranked_views views_source views_captured_at metrics_status }
  }
`;
export const BULK_APPROVE_CPM_RANKING_CLIPS = gql`
  mutation AdminBulkApproveCpmRankingClips($campaignId: ID!, $ids: [ID!]!) {
    adminBulkApproveCpmRankingClips(campaignId: $campaignId, ids: $ids) { approved skipped { id code } }
  }
`;
export const BULK_REJECT_CPM_RANKING_CLIPS = gql`
  mutation AdminBulkRejectCpmRankingClips($campaignId: ID!, $ids: [ID!]!, $reason: String) {
    adminBulkRejectCpmRankingClips(campaignId: $campaignId, ids: $ids, reason: $reason) { rejected skipped { id code } }
  }
`;
export const RESOLVE_CPM_RANKING_TIE = gql`
  mutation AdminResolveCpmRankingTie($campaignId: ID!, $participantIds: [ID!]!, $reason: String!) {
    adminResolveCpmRankingTie(campaignId: $campaignId, participantIds: $participantIds, reason: $reason) { campaign_id review_fingerprint }
  }
`;
export const FINALIZE_CPM_RANKING = gql`
  mutation AdminFinalizeCpmRanking($campaignId: ID!, $expectedFingerprint: String!) {
    adminFinalizeCpmRanking(campaignId: $campaignId, expectedFingerprint: $expectedFingerprint) { campaign_id phase finalized_at }
  }
`;
